import { useContext, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { ITreeData, TreeContext } from "./CustomDropdown";
import Icon from "../../Icon";

import styles from "./CustomTreeDropdown.module.css";

interface SelectionState {
  fullySelected: boolean;
  partiallySelected: boolean;
  selected: boolean;
}

export function LiItem({
  item,
  searchInflicted,
}: Readonly<{
  item: ITreeData;
  searchInflicted: boolean;
}>) {
  const { items } = useContext(TreeContext);
  const [open, setOpen] = useState(false);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const {
    handleSelect,
    selectedItems: prevSelectedItems,
    selectionMode,
  } = useContext(TreeContext);

  const getSelectionState = (itemToCheck = item): SelectionState => {
    const selectedItems = [...prevSelectedItems];
    const selected = selectedItems.includes(itemToCheck.key!.toString());

    if (!itemToCheck.children?.length) {
      return {
        fullySelected: selected,
        partiallySelected: false,
        selected: selected,
      };
    }

    let allChildrenSelected = true;
    let anyChildSelected = false;

    for (const child of itemToCheck.children) {
      const childState = getSelectionState(child);
      if (!childState.fullySelected) {
        allChildrenSelected = false;
      }
      if (childState.fullySelected || childState.partiallySelected) {
        anyChildSelected = true;
      }
    }

    return {
      fullySelected: selected && allChildrenSelected,
      partiallySelected:
        (anyChildSelected && !selected) || (selected && !allChildrenSelected),
      selected: selected,
    };
  };

  const selectedData = useMemo(() => getSelectionState(), [prevSelectedItems]);

  useEffect(() => {
    if (!open) {
      setOpen(
        searchInflicted ||
          (!selectedData.selected && selectedData.partiallySelected),
      );
    }
  }, [searchInflicted, selectedData.selected, selectedData.partiallySelected]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = selectedData.partiallySelected;
    }
  }, [selectedData.partiallySelected]);

  const checkboxHandleSelect = (el: ITreeData, arr: ITreeData[] = []) => {
    const elementsToSet: ITreeData[] = arr;
    elementsToSet.push(el);
    if (el.children && el.children.length > 0) {
      el.children.forEach((child: ITreeData) => {
        checkboxHandleSelect(child, elementsToSet);
      });
    }
    return elementsToSet;
  };

  const getItem = (
    key: string,
    itemsToCheck: ITreeData[],
  ): ITreeData | null => {
    for (const i of itemsToCheck) {
      if (i.key === key) {
        return i;
      }
      if (i.children?.length) {
        const foundItem = getItem(key, i.children);
        if (foundItem) {
          return foundItem;
        }
      }
    }
    return null;
  };

  return (
    <li
      data-cy="organisation-filter-list"
      key={item.key}
      className={styles.liElement}
      role="treeitem"
      aria-selected={selectedData.selected}
      aria-expanded={item.children?.length ? open : undefined}
    >
      <button
        type="button"
        data-cy="organisation-filter-item"
        data-tree-focusable
        className={classNames({
          [styles.itemWrapper]: true,
          [styles.itemSelected]: selectedData.selected,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSelect(item);
        }}
        onKeyDown={(e) => {
          switch (e.key) {
            case "Enter":
              e.preventDefault();
              e.stopPropagation();
              handleSelect(item);
              break;
            case " ":
              e.preventDefault();
              e.stopPropagation();
              if (
                selectionMode === "multiple" &&
                (item.children?.length || item.hasChildren)
              ) {
                const pureItem = getItem(item.key as string, items);
                const shouldSelect = selectedData.partiallySelected
                  ? true
                  : !selectedData.selected;
                handleSelect(
                  checkboxHandleSelect(pureItem ?? item),
                  shouldSelect,
                );
              } else {
                handleSelect(item);
              }
              break;
            case "ArrowRight":
              if (item.children?.length) {
                e.preventDefault();
                e.stopPropagation();
                if (open) {
                  const childList = (e.currentTarget as HTMLElement)
                    .nextElementSibling;
                  const firstChild = childList?.querySelector<HTMLElement>(
                    "[data-tree-focusable]",
                  );
                  firstChild?.focus();
                  firstChild?.scrollIntoView({ block: "nearest" });
                } else {
                  setOpen(true);
                }
              }
              break;
            case "ArrowLeft":
              if (open && item.children?.length) {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              }
              break;
          }
        }}
      >
        <span
          className={styles.expanderButtonWrapper}
          aria-hidden="true"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
        >
          <Icon
            name={open ? "chevron-down" : "chevron-right"}
            size={20}
            className={classNames({
              [styles.expanderButton]: true,
              [styles.hidden]: !item.children?.length,
            })}
          />
        </span>
        {selectionMode === "multiple" && (
          <div>
            <input
              ref={checkboxRef}
              id={`ch_${item.key}`}
              data-cy="organisation-filter-checkbox"
              type="checkbox"
              aria-label={item.label}
              className={classNames({
                [styles.treeElementCheckbox]: true,
                [styles.partially]: selectedData.partiallySelected,
                [styles.hidden]: !(item.children?.length || item.hasChildren),
              })}
              checked={selectedData.partiallySelected || selectedData.selected}
              onChange={(e) => {
                const pureItem = getItem(item.key as string, items);
                e.stopPropagation();
                const target = e.target as HTMLInputElement;
                // if partially selected, select the item and all its children
                const shouldSelect = selectedData.partiallySelected
                  ? true
                  : target.checked;
                handleSelect(
                  checkboxHandleSelect(pureItem ?? item),
                  shouldSelect,
                );
              }}
            />
          </div>
        )}
        <span
          data-cy="organisation-filter-label"
          className={styles.treeElementLabel}
        >
          {item.label}
        </span>
        <span
          aria-hidden="true"
          className={classNames({
            [styles.checkIconWrapper]: true,
            [styles.isSelected]: selectedData.selected,
          })}
        >
          <Icon
            name="check"
            color="Primary-700"
            className={classNames({
              [styles.checkIcon]: true,
              [styles.hidden]: !selectedData.selected,
            })}
          />
        </span>
      </button>
      {open && item.children && item.children.length > 0 && (
        <ul
          role="group"
          className={styles.subElements}
          data-cy="organisation-filter-childsList"
        >
          {item.children.map((child: ITreeData) => (
            <LiItem
              key={child.key}
              item={child}
              searchInflicted={searchInflicted}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
